import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { resetPassword } = useAuth();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);

    useEffect(() => {
        const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

        if (window.turnstile && turnstileRef.current && token && !widgetIdRef.current) {
            widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
                sitekey: siteKey,
                callback: (token) => setTurnstileToken(token),
                'expired-callback': () => setTurnstileToken(''),
                'error-callback': () => setTurnstileToken(''),
                theme: 'dark'
            });
        }
    }, [token]);

    const validatePassword = (pass) => {
        if (pass.length < 8) return "Password must be at least 8 characters";
        if (!/[A-Z]/.test(pass)) return "Password must contain an uppercase letter";
        if (!/[a-z]/.test(pass)) return "Password must contain a lowercase letter";
        if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(pass)) return "Password must contain a digit or special character";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!token) {
            setError('Missing reset token.');
            return;
        }

        if (!turnstileToken) {
            setError('Please complete the bot challenge');
            return;
        }

        const passError = validatePassword(password);
        if (passError) {
            setError(passError);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            const res = await resetPassword(token, password, turnstileToken);
            setMessage(res.message || 'Password reset successful! You can now login.');
            setTimeout(() => navigate('/'), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Reset failed. The link may be expired.');
            if (widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
            setTurnstileToken('');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <div className="bg-bg-panel p-12 rounded-lg text-center max-w-md border border-border-light shadow-xl">
                    <h2 className="text-2xl font-bold mb-4 text-accent-red">Error</h2>
                    <p className="text-text-muted mb-8">Missing reset token.</p>
                    <button onClick={() => navigate('/')} className="px-6 py-2 bg-accent-blue text-white rounded font-semibold">
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex justify-center items-center min-h-[60vh] px-4">
            <div className="bg-bg-panel p-8 md:p-12 rounded-lg max-w-md w-full border border-border-light shadow-xl">
                <h2 className="text-2xl font-bold mb-6 text-white text-center">Reset Password</h2>
                <p className="text-text-muted text-center mb-8">Enter your new password below.</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">New Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full p-3 bg-bg-darkest border border-border-light rounded text-white focus:outline-none focus:border-accent-blue"
                            placeholder="••••••••"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">Confirm New Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            className="w-full p-3 bg-bg-darkest border border-border-light rounded text-white focus:outline-none focus:border-accent-blue"
                            placeholder="••••••••"
                        />
                    </div>

                    <div className="flex justify-center py-2">
                        <div ref={turnstileRef}></div>
                    </div>

                    {error && (
                        <div className="p-3 bg-accent-red/20 border border-accent-red/50 rounded text-accent-red text-sm">
                            {error}
                        </div>
                    )}

                    {message && (
                        <div className="p-3 bg-green-900/20 border border-green-500/50 rounded text-green-400 text-sm">
                            {message}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-3 rounded font-bold text-white transition-all ${
                            loading ? 'bg-gray-600 cursor-not-allowed' : 'bg-accent-blue hover:bg-blue-600'
                        }`}
                    >
                        {loading ? 'Processing...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
